import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ModalService } from '../../../base/shared/modal/modal.service';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { RippleModule } from 'primeng/ripple';
import { ToastModule } from 'primeng/toast';
import { FilterDTO, FilterOperator, SortDirection } from '../../../base/models/models.swagger.generated';
import { OrderFormType } from './order-edit-form';
import { PagedQueryModel } from '../../../base/shared/table/paged-query-model';
import { FilterMap } from '../../../base/shared/table/filter-map';
import { FilterMetadata } from 'primeng/api';
import { Subscription } from 'rxjs';
import { TooltipModule } from 'primeng/tooltip';
import { SearchFieldComponent } from '../../../base/shared/search-field/search-field.component';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import {
  BuyerOrderSummaryDTO,
  SellerOrderSummaryDTO,
  UpdateOrderStatusDTO,
  CancelOrderDTO,
  OrderDTO,
  OrderStatus,
  OrderLimitedDTO,
} from '../../../base/models/models.swagger.generated';
import { OrderService } from './order.service';
import { AuthService } from 'src/app/base/services/auth.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-order',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ToolbarModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    ToastModule,
    TranslateModule,
    SearchFieldComponent,
    CommonModule,
    RippleModule,
    TooltipModule,
    DropdownModule,
    InputTextareaModule,
    MenuModule
  ],
  templateUrl: './order.component.html',
  styleUrl: './order.component.scss'
})
export class OrderComponent implements OnInit, OnDestroy {
  public data: OrderLimitedDTO[] = [];
  public totalNoOfRecords: number = 0;
  public order: OrderDTO | null = null;
  public itemDialog: boolean = false;
  public pagedQuery: PagedQueryModel = PagedQueryModel.getDefault();
  public form: FormGroup<OrderFormType>;
  public columnFilters: { [key: string]: FilterMetadata | FilterMetadata[] } = {};
  public globalFilters = [
    'Order Number',
    'buyerName',
    'sellerName',
    'status',
    'orderDate',
    'contactNumber',
    'deliveryAddress',
    'deliveryInstructions',
    'cancellationReason',
    'productName'
  ];
  private _subscription: Subscription = new Subscription();
  private searchText: string = '';
  OrderStatus = OrderStatus;
  expandedRowId: number | null = null;
  public orderDialog: boolean = false;
  public orderForm: FormGroup<OrderFormType>;
  public selectedOrder: OrderLimitedDTO | null = null;
  public showCancellationReason: boolean = false;

  public statusOptions = [
    { label: 'Pending', value: OrderStatus.Pending },
    { label: 'Processing', value: OrderStatus.Processing },
    { label: 'Out for Delivery', value: OrderStatus.OutForDelivery },
    { label: 'Delivered', value: OrderStatus.Delivered }
    // { label: 'Cancelled', value: OrderStatus.Cancelled }
  ];

  public filteredStatusOptions: any[] = [];

  public statusFilterOptions = [
    { label: 'Pending', value: OrderStatus.Pending },
    { label: 'Processing', value: OrderStatus.Processing },
    { label: 'Out for Delivery', value: OrderStatus.OutForDelivery },
    { label: 'Delivered', value: OrderStatus.Delivered },
    { label: 'Cancelled', value: OrderStatus.Cancelled }
  ];

  public exportMenuItems: MenuItem[] = [];

  get formControls() {
    return this.orderForm?.controls;
  }

  constructor(
    private orderService: OrderService,
    private router: Router,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private modalService: ModalService,
    public authService: AuthService
  ) { }

  ngOnInit() {
    this.loadData();
    this.initializeExportMenu();
  }

  private initializeExportMenu() {
    this.exportMenuItems = [
      {
        label: 'Export Order Summary to Excel',
        icon: 'pi pi-file-excel',
        command: () => this.exportOrderSummaryToExcel()
      },
      {
        label: 'Export Order Summary to CSV',
        icon: 'pi pi-file',
        command: () => this.exportOrderSummaryToCSV()
      },
      {
        label: 'Export Order Summary to PDF',
        icon: 'pi pi-file-pdf',
        command: () => this.exportOrderSummaryToPDF()
      },
      {
        separator: true
      },
      {
        label: 'Export Product Details to Excel',
        icon: 'pi pi-file-excel',
        command: () => this.exportProductDetailsToExcel()
      },
      {
        label: 'Export Product Details to CSV',
        icon: 'pi pi-file',
        command: () => this.exportProductDetailsToCSV()
      },
      {
        label: 'Export Product Details to PDF',
        icon: 'pi pi-file-pdf',
        command: () => this.exportProductDetailsToPDF()
      }
    ];
  }

  private loadData() {
    console.log('Loading data with pagedQuery:', this.pagedQuery);
    this._subscription.add(
      this.orderService.searchOrders(
        this.pagedQuery.filters,
        this.pagedQuery.skip,
        this.pagedQuery.take,
        this.pagedQuery.sortBy,
        this.pagedQuery.sortDirection
      ).subscribe({
        next: (data) => {
          console.log('API response:', data);
          this.data = data.items ?? [];
          this.totalNoOfRecords = data.totalNumberOfItems ?? this.data.length;
        },
        error: (err) => console.error('Error in loadData:', err)
      })
    );
  }

  public get filterMatchModeOptions() {
    return FilterMap.getStringFilterMatchModeOptions();
  }

  private getFilters(): FilterDTO[] {
    const filters: FilterDTO[] = [];
    const addFilter = (field: string, operator: FilterOperator, value: any) => {
      if (value !== null && value !== undefined && value !== '') {
        filters.push(new FilterDTO({ field, operator, value, isComplex: false }));
      }
    };

    if (this.searchText && this.searchText.trim() !== '') {
      const searchValue = this.searchText.trim();
      const globalFields = [
        'orderNumber',
        'buyerName',
        'sellerName',
        'status',
        'totalAmount',
        'deliveryAddress',
        'deliveryInstructions',
        'cancellationReason',
        'productName',
        'contactNumber'
      ];

      const orFilters = globalFields.map(
        (field) =>
          new FilterDTO({
            field,
            operator: FilterOperator.Contains,
            value: searchValue,
            isComplex: true,
          })
      );

      filters.push(...orFilters);
    }

    Object.entries(this.columnFilters).forEach(([key, filter]) => {
      if (Array.isArray(filter)) {
        filter.forEach((f) => {
          if (f.value !== null && f.value !== undefined && f.value !== '') {
            if (key === 'orderDate' && f.value instanceof Date) {
              addFilter(key, FilterMap.getFilterOperator(f.matchMode), f.value.toISOString());
            } else {
              addFilter(key, FilterMap.getFilterOperator(f.matchMode), f.value);
            }
          }
        });
      } else if (filter?.value !== null && filter?.value !== undefined && filter?.value !== '') {
        if (key === 'orderDate' && filter.value instanceof Date) {
          addFilter(key, FilterMap.getFilterOperator(filter.matchMode), filter.value.toISOString());
        } else {
          addFilter(key, FilterMap.getFilterOperator(filter.matchMode), filter.value);
        }
      }
    });

    console.log('Generated filters:', filters);
    return filters;
  }

  public onFilter(event: any) {
    console.log('Filter event:', event);

    if (event.first !== undefined) {
      this.pagedQuery.skip = event.first;
    }
    if (event.rows !== undefined) {
      this.pagedQuery.take = event.rows;
    }

    if (event.filters) {
      this.columnFilters = event.filters;
      this.pagedQuery.filters = this.getFilters();

      if (!event.first && !event.rows) {
        this.pagedQuery.skip = 0;
      }
    }

    console.log('PagedQuery:', this.pagedQuery);
    this.loadData();
  }

  toggleRow(orderId: number): void {
    if (this.expandedRowId === orderId) {
      this.expandedRowId = null;
    } else {
      this.expandedRowId = orderId;
    }
  }

  public hideDialog() {
    this.itemDialog = false;
  }

  public openEdit(order: OrderLimitedDTO) {
    this.selectedOrder = order;
    this.showCancellationReason = order.status === OrderStatus.Cancelled;
    this.filterStatusOptions(order.status);
    this.initOrderForm();

    if (order.status === OrderStatus.Delivered || order.status === OrderStatus.Cancelled) {
      this.orderForm.disable();
    }

    this.orderDialog = true;
  }

  private initOrderForm() {
    const { status, deliveryInstructions, cancellationReason } = this.selectedOrder || {};

    this.orderForm = this.fb.group<OrderFormType>({
      status: this.fb.control<number>(status ?? OrderStatus.Pending, Validators.required),
      deliveryInstructions: this.fb.control<string>(deliveryInstructions || ''),
      CancelationReason: this.fb.control<string>(cancellationReason || '')
    });
  }

  private filterStatusOptions(currentStatus: OrderStatus) {
    switch (currentStatus) {
      case OrderStatus.Pending:
        this.filteredStatusOptions = [...this.statusOptions];
        break;

      case OrderStatus.Processing:
        this.filteredStatusOptions = this.statusOptions.filter(
          option => option.value !== OrderStatus.Pending
        );
        break;

      case OrderStatus.OutForDelivery:
        this.filteredStatusOptions = this.statusOptions.filter(
          option => option.value !== OrderStatus.Pending &&
            option.value !== OrderStatus.Processing
        );
        break;

      case OrderStatus.Delivered:
      case OrderStatus.Cancelled:
        this.filteredStatusOptions = this.statusOptions.filter(
          option => option.value === currentStatus
        );
        break;

      default:
        this.filteredStatusOptions = [...this.statusOptions];
    }
  }

  public onDeleteClick() {
    this.showCancellationReason = true;
    this.orderForm.get('CancelationReason')?.setValidators([Validators.required]);
    this.orderForm.get('CancelationReason')?.updateValueAndValidity();
  }

  public saveOrder() {
    if (this.orderForm.valid && this.selectedOrder) {
      const formValue = this.orderForm.getRawValue();

      const updatedOrder = new UpdateOrderStatusDTO({
        orderId: this.selectedOrder.orderId,
        status: formValue.status,
        note: formValue.deliveryInstructions
      });

      this._subscription.add(
        this.orderService.updateOrderStatus(updatedOrder).subscribe({
          next: () => {
            console.log('Order updated successfully');
            this.loadData();
            this.hideOrderDialog();
          },
          error: (err) => {
            console.error('Error updating order:', err);
          },
        })
      );
    }
  }

  public cancelOrder(order?: OrderLimitedDTO) {
    const orderToCancel = order || this.selectedOrder;

    if (!orderToCancel) return;

    if (!this.showCancellationReason) {
      const dialog = this.modalService.showOkCancelDialog(
        'i18n.domain.order.message.cancel-confirm-title',
        'i18n.domain.order.message.cancel-confirm'
      );

      dialog.onOkSubject.subscribe(() => {
        this.performCancellation(orderToCancel, 'User cancelled the order');
      });
    } else {
      if (this.orderForm.valid) {
        const formValue = this.orderForm.getRawValue();
        this.performCancellation(orderToCancel, formValue.CancelationReason);
      }
    }
  }

  private performCancellation(order: OrderLimitedDTO, reason: string) {
    const dto = new CancelOrderDTO({
      orderId: order.orderId,
      cancellationReason: reason
    });

    this._subscription.add(
      this.orderService.cancelOrder(dto).subscribe({
        next: () => {
          console.log('Order cancelled successfully');
          this.loadData();
          this.hideOrderDialog();
        },
        error: (err) => {
          console.error('Error cancelling order:', err);
        },
      })
    );
  }

  public hideOrderDialog() {
    this.orderDialog = false;
    this.selectedOrder = null;
    this.orderForm?.enable();
    this.orderForm = null;
    this.showCancellationReason = false;
  }

  getStatusName(status: OrderStatus): string {
    switch (status) {
      case OrderStatus.Pending: return 'Pending';
      case OrderStatus.Processing: return 'Processing';
      case OrderStatus.OutForDelivery: return 'Out for Delivery';
      case OrderStatus.Delivered: return 'Delivered';
      case OrderStatus.Cancelled: return 'Cancelled';
      default: return 'Unknown';
    }
  }

  getProductStatusName(status: number): string {
    switch (status) {
      case 0: return 'Pending';
      case 1: return 'Processing';
      case 2: return 'Out for Delivery';
      case 3: return 'Delivered';
      case 4: return 'Cancelled';
      default: return 'Unknown';
    }
  }

  public onPageChange(event: any) {
    this.pagedQuery.skip = event.first;
    this.pagedQuery.take = event.rows;
    this.loadData();
  }

  public onSearchTextChanged(searchText: string) {
    this.searchText = searchText.trim();
    this.pagedQuery.filters = this.getFilters();
    this.pagedQuery.skip = 0;
    this.loadData();
  }

  public onSort(event: any) {
    this.pagedQuery.sortBy = event.field;
    this.pagedQuery.sortDirection = event.order === 1 ? SortDirection.Ascending : SortDirection.Descending;
    this.pagedQuery.skip = 0;
    this.loadData();
  }

  public exportOrderSummaryToExcel() {
    this._subscription.add(
      this.orderService.searchOrders(
        this.pagedQuery.filters,
        0,
        this.totalNoOfRecords,
        this.pagedQuery.sortBy,
        this.pagedQuery.sortDirection
      ).subscribe({
        next: (data) => {
          const allOrders = data.items ?? [];
          const exportData = this.prepareOrderSummaryExportDataFromOrders(allOrders);
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Order Summary');

          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const fileName = `Order_Summary_${new Date().toISOString().split('T')[0]}.xlsx`;
          saveAs(blob, fileName);
        },
        error: (err) => console.error('Error exporting to Excel:', err)
      })
    );
  }

  public exportOrderSummaryToCSV() {
    this._subscription.add(
      this.orderService.searchOrders(
        this.pagedQuery.filters,
        0,
        this.totalNoOfRecords,
        this.pagedQuery.sortBy,
        this.pagedQuery.sortDirection
      ).subscribe({
        next: (data) => {
          const allOrders = data.items ?? [];
          const exportData = this.prepareOrderSummaryExportDataFromOrders(allOrders);
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const csvOutput = XLSX.utils.sheet_to_csv(worksheet);

          const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
          const fileName = `Order_Summary_${new Date().toISOString().split('T')[0]}.csv`;
          saveAs(blob, fileName);
        },
        error: (err) => console.error('Error exporting to CSV:', err)
      })
    );
  }

  public exportOrderSummaryToPDF() {
    this._subscription.add(
      this.orderService.searchOrders(
        this.pagedQuery.filters,
        0,
        this.totalNoOfRecords,
        this.pagedQuery.sortBy,
        this.pagedQuery.sortDirection
      ).subscribe({
        next: (data) => {
          const allOrders = data.items ?? [];
          this.generateOrderSummaryPDF(allOrders);
        },
        error: (err) => console.error('Error exporting to PDF:', err)
      })
    );
  }

  private generateOrderSummaryPDF(orders: OrderLimitedDTO[]) {
    const doc = new jsPDF('l', 'mm', 'a4');

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Order Summary Report', 14, 15);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 14, 22);
    doc.text(`Total Orders: ${orders.length}`, 14, 27);

    const tableData = orders.map(order => [
      order.orderId?.toString() || '-',
      order.buyerName || '-',
      order.sellerName || '-',
      this.formatOrderDate(order.orderDate),
      `₹${order.totalAmount?.toFixed(2) || '0.00'}`,
      this.getStatusName(order.status),
      order.contactNumber || '-',
      order.deliveryAddress || '-'
    ]);

    autoTable(doc, {
      head: [['Order ID', 'Buyer', 'Seller', 'Order Date', 'Amount', 'Status', 'Contact', 'Address']],
      body: tableData,
      startY: 32,
      styles: {
        fontSize: 8,
        cellPadding: 2
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 35 },
        2: { cellWidth: 35 },
        3: { cellWidth: 35 },
        4: { cellWidth: 25 },
        5: { cellWidth: 30 },
        6: { cellWidth: 30 },
        7: { cellWidth: 50 }
      }
    });

    const fileName = `Order_Summary_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  }

  private prepareOrderSummaryExportDataFromOrders(orders: OrderLimitedDTO[]) {
    return orders.map(order => ({
      'Order ID': order.orderId,
      'Buyer Name': order.buyerName,
      'Seller Name': order.sellerName || '-',
      'Order Date': this.formatOrderDate(order.orderDate),
      'Total Amount (₹)': order.totalAmount,
      'Status': this.getStatusName(order.status),
      'Contact Number': order.contactNumber,
      'Delivery Address': order.deliveryAddress || '-',
      'Delivery Instructions': order.deliveryInstructions || '-',
      'Cancellation Reason': order.cancellationReason || '-',
      'Products': order.items?.map(item =>
        `${item.productName} (Qty: ${item.quantity}, Price: ₹${item.unitPrice})`
      ).join('; ') || '-'
    }));
  }

  public exportProductDetailsToExcel() {
    this._subscription.add(
      this.orderService.searchOrders(
        this.pagedQuery.filters,
        0,
        this.totalNoOfRecords,
        this.pagedQuery.sortBy,
        this.pagedQuery.sortDirection
      ).subscribe({
        next: (data) => {
          const allOrders = data.items ?? [];
          const exportData = this.prepareProductDetailsExportDataFromOrders(allOrders);
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Product Details');

          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const fileName = `Product_Details_${new Date().toISOString().split('T')[0]}.xlsx`;
          saveAs(blob, fileName);
        },
        error: (err) => console.error('Error exporting to Excel:', err)
      })
    );
  }

  public exportProductDetailsToCSV() {
    this._subscription.add(
      this.orderService.searchOrders(
        this.pagedQuery.filters,
        0,
        this.totalNoOfRecords,
        this.pagedQuery.sortBy,
        this.pagedQuery.sortDirection
      ).subscribe({
        next: (data) => {
          const allOrders = data.items ?? [];
          const exportData = this.prepareProductDetailsExportDataFromOrders(allOrders);
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const csvOutput = XLSX.utils.sheet_to_csv(worksheet);

          const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
          const fileName = `Product_Details_${new Date().toISOString().split('T')[0]}.csv`;
          saveAs(blob, fileName);
        },
        error: (err) => console.error('Error exporting to CSV:', err)
      })
    );
  }

  public exportProductDetailsToPDF() {
    this._subscription.add(
      this.orderService.searchOrders(
        this.pagedQuery.filters,
        0,
        this.totalNoOfRecords,
        this.pagedQuery.sortBy,
        this.pagedQuery.sortDirection
      ).subscribe({
        next: (data) => {
          const allOrders = data.items ?? [];
          this.generateProductDetailsPDF(allOrders);
        },
        error: (err) => console.error('Error exporting to PDF:', err)
      })
    );
  }

  private generateProductDetailsPDF(orders: OrderLimitedDTO[]) {
    const doc = new jsPDF('l', 'mm', 'a4');

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Product Details Report', 14, 15);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 14, 22);

    const tableData: any[] = [];
    orders.forEach(order => {
      if (order.items && order.items.length > 0) {
        order.items.forEach(item => {
          tableData.push([
            order.orderId?.toString() || '-',
            order.buyerName || '-',
            order.sellerName || '-',
            this.formatOrderDate(order.orderDate),
            this.getStatusName(order.status),
            item.productName || '-',
            item.quantity?.toString() || '-',
            `₹${item.unitPrice?.toFixed(2) || '0.00'}`,
            `₹${item.totalPrice?.toFixed(2) || '0.00'}`,
            this.getProductStatusName(item.status)
          ]);
        });
      } else {
        tableData.push([
          order.orderId?.toString() || '-',
          order.buyerName || '-',
          order.sellerName || '-',
          this.formatOrderDate(order.orderDate),
          this.getStatusName(order.status),
          '-', '-', '-', '-', '-'
        ]);
      }
    });

    doc.text(`Total Products: ${tableData.length}`, 14, 27);

    autoTable(doc, {
      head: [['Order ID', 'Buyer', 'Seller', 'Date', 'Order Status', 'Product', 'Qty', 'Unit Price', 'Total', 'Status']],
      body: tableData,
      startY: 32,
      styles: {
        fontSize: 7,
        cellPadding: 1.5
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 28 },
        2: { cellWidth: 28 },
        3: { cellWidth: 30 },
        4: { cellWidth: 25 },
        5: { cellWidth: 35 },
        6: { cellWidth: 15 },
        7: { cellWidth: 22 },
        8: { cellWidth: 22 },
        9: { cellWidth: 25 }
      }
    });

    const fileName = `Product_Details_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  }

  private prepareProductDetailsExportDataFromOrders(orders: OrderLimitedDTO[]) {
    const productDetails: any[] = [];

    orders.forEach(order => {
      if (order.items && order.items.length > 0) {
        order.items.forEach(item => {
          productDetails.push({
            'Order ID': order.orderId,
            'Buyer Name': order.buyerName,
            'Seller Name': order.sellerName || '-',
            'Order Date': this.formatOrderDate(order.orderDate),
            'Order Status': this.getStatusName(order.status),
            'Contact Number': order.contactNumber,
            'Delivery Address': order.deliveryAddress || '-',
            'Product Name': item.productName,
            'Quantity': item.quantity,
            'Unit Price (₹)': item.unitPrice,
            'Total Price (₹)': item.totalPrice,
            'Product Status': this.getProductStatusName(item.status)
          });
        });
      } else {
        productDetails.push({
          'Order ID': order.orderId,
          'Buyer Name': order.buyerName,
          'Seller Name': order.sellerName || '-',
          'Order Date': this.formatOrderDate(order.orderDate),
          'Order Status': this.getStatusName(order.status),
          'Contact Number': order.contactNumber,
          'Delivery Address': order.deliveryAddress || '-',
          'Product Name': '-',
          'Quantity': '-',
          'Unit Price (₹)': '-',
          'Total Price (₹)': '-',
          'Product Status': '-'
        });
      }
    });

    return productDetails;
  }

  private formatOrderDate(orderDate: any): string {
    try {
      if (orderDate && typeof orderDate === 'object' && 'toJSDate' in orderDate) {
        return orderDate.toJSDate().toLocaleString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      if (orderDate instanceof Date) {
        return orderDate.toLocaleString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }

      if (typeof orderDate === 'string') {
        return new Date(orderDate).toLocaleString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return '-';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  }

  ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }
}